export const FIELD_LIMITS = {
  shortText: 120,
  mediumText: 240,
  longText: 5000,
  summary: 2000,
  listItem: 160,
  email: 254,
  phone: 32,
  url: 240,
  postalCode: 24,
  year: 4,
  hours: 3,
  gpa: 4,
};

export const ARRAY_LIMITS = {
  educationHistory: 25,
  workHistory: 40,
  volunteerWork: 30,
  skills: 80,
  selectedSkills: 80,
  certifications: 50,
  training: 80,
  references: 20,
  languages: 40,
  organizations: 60,
  interests: 60,
  awards: 60,
  significantProjects: 20,
  relevantCourses: 40,
};

export const PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^[0-9()+.\-\s]{7,32}$/,
  year: /^(19|20|21)\d{2}$/,
  hours: /^(?:0|[1-9]\d?|1[0-5]\d|16[0-8])$/,
  gpa: /^(?:[0-3](?:\.\d{1,2})?|4(?:\.0{1,2})?)$/,
};

const HTML_TAGS = /<[^>]*>/g;

const stripControlCharacters = (value) =>
  Array.from(value).filter((char) => {
    const code = char.charCodeAt(0);
    return code === 9 || code === 10 || code === 13 || (code > 31 && code !== 127);
  }).join('');

export const sanitizeText = (value, maxLength = FIELD_LIMITS.mediumText) => {
  if (value === null || value === undefined) return '';
  return stripControlCharacters(String(value))
    .replace(HTML_TAGS, '')
    .replace(/[<>]/g, '')
    .slice(0, maxLength);
};

export const sanitizeLine = (value, maxLength = FIELD_LIMITS.mediumText) =>
  sanitizeText(value, maxLength).replace(/\s+/g, ' ').trimStart();

export const sanitizeMultiline = (value, maxLength = FIELD_LIMITS.longText) =>
  sanitizeText(value, maxLength)
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n');

export const sanitizeIntegerText = (value, min, max) => {
  const digits = sanitizeText(value, 12).replace(/[^\d]/g, '');
  if (!digits) return '';
  const bounded = Math.min(Math.max(Number(digits), min), max);
  return Number.isFinite(bounded) ? String(bounded) : '';
};

export const sanitizeGpaText = (value) => {
  const cleaned = sanitizeText(value, FIELD_LIMITS.gpa).replace(/[^\d.]/g, '');
  if (!cleaned) return '';
  const parts = cleaned.split('.');
  const normalized = parts.length > 1
    ? `${parts[0]}.${parts.slice(1).join('').slice(0, 2)}`
    : parts[0];
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return '';
  return String(Math.min(Math.max(numeric, 0), 4));
};

export const sanitizeYearText = (value) =>
  sanitizeText(value, FIELD_LIMITS.year).replace(/[^\d]/g, '').slice(0, 4);

const limitArray = (value, limit) =>
  (Array.isArray(value) ? value : []).slice(0, limit);

const cleanStringArray = (value, limit, maxLength = FIELD_LIMITS.listItem) =>
  limitArray(value, limit)
    .map((item) => sanitizeLine(item, maxLength))
    .filter((item, index, arr) => item || index < arr.length);

const cleanDateValue = (value) => sanitizeLine(value, 10);

const cleanEducation = (entry = {}) => ({
  ...entry,
  showOnResume: entry?.showOnResume !== false,
  institution: sanitizeLine(entry.institution, FIELD_LIMITS.mediumText),
  degree: sanitizeLine(entry.degree, FIELD_LIMITS.mediumText),
  major: sanitizeLine(entry.major, FIELD_LIMITS.mediumText),
  minor: sanitizeLine(entry.minor, FIELD_LIMITS.mediumText),
  gpa: sanitizeGpaText(entry.gpa),
  graduationYear: sanitizeYearText(entry.graduationYear),
  startDate: cleanDateValue(entry.startDate),
  endDate: cleanDateValue(entry.endDate),
  significantProjects: cleanStringArray(
    entry.significantProjects,
    ARRAY_LIMITS.significantProjects,
    FIELD_LIMITS.longText,
  ),
  relevantCourses: cleanStringArray(
    entry.relevantCourses,
    ARRAY_LIMITS.relevantCourses,
    FIELD_LIMITS.listItem,
  ),
});

const cleanWork = (entry = {}) => ({
  ...entry,
  showOnResume: entry?.showOnResume !== false,
  jobTitle: sanitizeLine(entry.jobTitle, FIELD_LIMITS.mediumText),
  employer: sanitizeLine(entry.employer, FIELD_LIMITS.mediumText),
  employerAddress: sanitizeLine(entry.employerAddress, FIELD_LIMITS.mediumText),
  startDate: cleanDateValue(entry.startDate),
  endDate: cleanDateValue(entry.endDate),
  hoursPerWeek: sanitizeIntegerText(entry.hoursPerWeek, 0, 168),
  responsibilities: sanitizeMultiline(entry.responsibilities, FIELD_LIMITS.longText),
  keyAccomplishments: sanitizeMultiline(entry.keyAccomplishments, FIELD_LIMITS.longText),
  payAmount: sanitizeLine(entry.payAmount, FIELD_LIMITS.shortText),
  supervisor: sanitizeLine(entry.supervisor, FIELD_LIMITS.mediumText),
  supervisorContact: sanitizeLine(entry.supervisorContact, FIELD_LIMITS.mediumText),
});

const cleanVolunteer = (entry = {}) => ({
  ...entry,
  organization: sanitizeLine(entry.organization, FIELD_LIMITS.mediumText),
  role: sanitizeLine(entry.role, FIELD_LIMITS.mediumText),
  startDate: cleanDateValue(entry.startDate),
  endDate: cleanDateValue(entry.endDate),
  hoursPerWeek: sanitizeIntegerText(entry.hoursPerWeek, 0, 168),
  responsibilities: sanitizeMultiline(entry.responsibilities, FIELD_LIMITS.longText),
});

const cleanCertification = (entry = {}) => ({
  ...entry,
  certificationName: sanitizeLine(entry.certificationName, FIELD_LIMITS.mediumText),
  certificationAuthority: sanitizeLine(entry.certificationAuthority, FIELD_LIMITS.mediumText),
  issueDate: cleanDateValue(entry.issueDate),
  expiryDate: cleanDateValue(entry.expiryDate),
});

const cleanReference = (entry = {}) => ({
  ...entry,
  name: sanitizeLine(entry.name, FIELD_LIMITS.mediumText),
  relationship: sanitizeLine(entry.relationship, FIELD_LIMITS.mediumText),
  email: sanitizeLine(entry.email, FIELD_LIMITS.email),
  phone: sanitizeLine(entry.phone, FIELD_LIMITS.phone),
  phoneNumber: sanitizeLine(entry.phoneNumber, FIELD_LIMITS.phone),
  notes: sanitizeMultiline(entry.notes, FIELD_LIMITS.summary),
});

export const sanitizeResumeFormData = (data = {}) => ({
  ...data,
  personalInfo: {
    ...(data.personalInfo || {}),
    firstName: sanitizeLine(data.personalInfo?.firstName, FIELD_LIMITS.shortText),
    lastName: sanitizeLine(data.personalInfo?.lastName, FIELD_LIMITS.shortText),
    phoneNumber: sanitizeLine(data.personalInfo?.phoneNumber, FIELD_LIMITS.phone),
    email: sanitizeLine(data.personalInfo?.email, FIELD_LIMITS.email),
    socialPresence: sanitizeLine(data.personalInfo?.socialPresence, FIELD_LIMITS.url),
    street: sanitizeLine(data.personalInfo?.street, FIELD_LIMITS.mediumText),
    city: sanitizeLine(data.personalInfo?.city, FIELD_LIMITS.shortText),
    state: sanitizeLine(data.personalInfo?.state, FIELD_LIMITS.shortText),
    postalCode: sanitizeLine(data.personalInfo?.postalCode, FIELD_LIMITS.postalCode),
    country: sanitizeLine(data.personalInfo?.country, FIELD_LIMITS.shortText),
  },
  professionalSummary: sanitizeMultiline(data.professionalSummary, FIELD_LIMITS.summary),
  militaryInfo: {
    ...(data.militaryInfo || {}),
    militaryOccupation: sanitizeLine(data.militaryInfo?.militaryOccupation, FIELD_LIMITS.mediumText),
    militaryDuration: sanitizeLine(data.militaryInfo?.militaryDuration, FIELD_LIMITS.shortText),
    militaryLocation: sanitizeLine(data.militaryInfo?.militaryLocation, FIELD_LIMITS.mediumText),
  },
  educationHistory: limitArray(data.educationHistory, ARRAY_LIMITS.educationHistory).map(cleanEducation),
  workHistory: limitArray(data.workHistory, ARRAY_LIMITS.workHistory).map(cleanWork),
  volunteerWork: limitArray(data.volunteerWork, ARRAY_LIMITS.volunteerWork).map(cleanVolunteer),
  skills: cleanStringArray(data.skills, ARRAY_LIMITS.skills),
  selectedSkills: cleanStringArray(data.selectedSkills, ARRAY_LIMITS.selectedSkills),
  certifications: limitArray(data.certifications, ARRAY_LIMITS.certifications).map(cleanCertification),
  training: cleanStringArray(data.training, ARRAY_LIMITS.training),
  references: limitArray(data.references, ARRAY_LIMITS.references).map(cleanReference),
  languages: cleanStringArray(data.languages, ARRAY_LIMITS.languages),
  organizations: cleanStringArray(data.organizations, ARRAY_LIMITS.organizations),
  interests: cleanStringArray(data.interests, ARRAY_LIMITS.interests),
  awards: cleanStringArray(data.awards, ARRAY_LIMITS.awards),
  additionalInformation: sanitizeMultiline(data.additionalInformation, FIELD_LIMITS.longText),
});

export const hasResumeEvidence = (data = {}) => {
  const clean = sanitizeResumeFormData(data);
  return Boolean(
    clean.professionalSummary ||
      clean.skills?.some(Boolean) ||
      clean.educationHistory?.some((entry) =>
        entry.showOnResume !== false &&
        [
          entry.institution,
          entry.degree,
          entry.major,
          ...(entry.significantProjects || []),
          ...(entry.relevantCourses || []),
        ].some(Boolean),
      ) ||
      clean.workHistory?.some((entry) =>
        entry.showOnResume !== false &&
        [entry.jobTitle, entry.employer, entry.responsibilities, entry.keyAccomplishments].some(Boolean),
      ) ||
      clean.volunteerWork?.some((entry) =>
        [entry.organization, entry.role, entry.responsibilities].some(Boolean),
      ) ||
      clean.certifications?.some((entry) =>
        [entry.certificationName, entry.certificationAuthority].some(Boolean),
      ) ||
      ['training', 'languages', 'organizations', 'interests', 'awards'].some((key) =>
        clean[key]?.some(Boolean),
      )
  );
};
